import { useEffect, type RefObject } from 'react';

export interface UseOutsideClickOptions {
  /** When false, the listener is inert. Default: true. */
  enabled?: boolean;
  /** Event to bind. 'pointerdown' catches both mouse/touch before click. */
  event?: 'pointerdown' | 'mousedown' | 'click';
  /** Extra refs whose subtrees also count as "inside". Useful when a
   *  trigger lives outside the primary container (e.g., a tooltip anchor). */
  extraRefs?: ReadonlyArray<RefObject<HTMLElement | null>>;
}

/** Invokes `onOutside` when a pointer event lands outside the provided
 *  element(s). Ignores events retargeted through shadow DOM so open
 *  popovers inside web components don't false-trigger.
 *
 *  The handler deliberately runs on the document, not the window, so
 *  portalled overlays mounted to `document.body` are still captured. */
export function useOutsideClick<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutside: (event: Event) => void,
  options: UseOutsideClickOptions = {},
): void {
  const { enabled = true, event = 'pointerdown', extraRefs } = options;

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof document === 'undefined') return undefined;

    const handler = (e: Event): void => {
      const path = 'composedPath' in e ? e.composedPath() : undefined;
      const primary = ref.current;
      const contains = (node: HTMLElement | null): boolean => {
        if (node === null) return false;
        if (path !== undefined) return path.includes(node);
        return node.contains(e.target as Node);
      };
      if (contains(primary)) return;
      if (extraRefs !== undefined) {
        for (const r of extraRefs) {
          if (contains(r.current)) return;
        }
      }
      onOutside(e);
    };

    document.addEventListener(event, handler, true);
    return () => {
      document.removeEventListener(event, handler, true);
    };
  }, [ref, onOutside, enabled, event, extraRefs]);
}
